import { useAuth } from '@berky/auth'
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Combobox,
  DataTable,
  LoadingSkeleton,
  MetricCard,
  MetricCardGrid,
  PageHeader,
  ResponsiveTabsList,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsTrigger,
  formatDateTime,
  toast,
  type DataTableColumn,
} from '@berky/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { Navigate } from 'react-router'

import {
  type FailedNotification,
  type NotificationStatus,
  type RegisteredNotificationType,
  useNotificationsApi,
} from './api'
import { HomepageNewsSection } from './homepage-news-section'
import { NotificationHistorySection } from './notification-history-section'
import { NotificationPolicyCard } from './notification-policy-card'
import { roleAccess } from '../module-access'
import {
  PLATFORM_ADMIN_ROLE,
  usePlatformApi,
  type PlattformNutzer,
} from '../verwaltung/api'

const PERSON_SEARCH_DEBOUNCE_MS = 300

export function NotificationsPage() {
  const { user, isLoading } = useAuth()
  const access = roleAccess(user?.roles, [PLATFORM_ADMIN_ROLE])

  if (isLoading || access === 'unbekannt') {
    return <LoadingSkeleton variant="text" rows={4} />
  }
  if (access !== 'erlaubt') {
    return (
      <Navigate to="/forbidden" replace state={{ moduleTitle: 'Benachrichtigungen' }} />
    )
  }

  return (
    <>
      <PageHeader
        title="Benachrichtigungen"
        description="Mailversand, Startseiten-News und Modulverantwortliche verwalten."
      />
      <Tabs defaultValue="status">
        <ResponsiveTabsList aria-label="Bereiche der Benachrichtigungsverwaltung">
          <TabsTrigger value="status">Betriebszustand</TabsTrigger>
          <TabsTrigger value="failed">Fehlgeschlagen</TabsTrigger>
          <TabsTrigger value="history">Versandprotokoll</TabsTrigger>
          <TabsTrigger value="contacts">Verantwortliche</TabsTrigger>
          <TabsTrigger value="types">Arten</TabsTrigger>
          <TabsTrigger value="homepage-news">Startseiten-News</TabsTrigger>
        </ResponsiveTabsList>
        <TabsContent value="status">
          <StatusSection />
        </TabsContent>
        <TabsContent value="failed">
          <FailedSection />
        </TabsContent>
        <TabsContent value="history">
          <NotificationHistorySection />
        </TabsContent>
        <TabsContent value="contacts">
          <ContactsSection />
        </TabsContent>
        <TabsContent value="types">
          <TypesSection />
        </TabsContent>
        <TabsContent value="homepage-news">
          <HomepageNewsSection />
        </TabsContent>
      </Tabs>
    </>
  )
}

function StatusSection() {
  const api = useNotificationsApi()
  const queryClient = useQueryClient()
  const status = useQuery({
    queryKey: ['notifications', 'status'],
    queryFn: ({ signal }) => api.get<NotificationStatus>('/status', { signal }),
  })
  const pause = useMutation({
    mutationFn: (paused: boolean) => api.put('/pause', { paused }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['notifications'] })
      toast.success('Versandzustand gespeichert')
    },
  })
  const testMail = useMutation({
    mutationFn: () => api.post('/test-mail', {}),
    onSuccess: () => toast.success('Testmail wurde eingereiht'),
  })

  if (status.isPending) return <LoadingSkeleton variant="card" rows={3} />
  if (status.isError || !status.data) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Status nicht verfügbar</AlertTitle>
        <AlertDescription>
          Der Benachrichtigungsdienst konnte nicht geladen werden.
        </AlertDescription>
      </Alert>
    )
  }

  const data = status.data
  const healthy = data.channelStatus === 'Available'
  return (
    <div className="space-y-4">
      {!healthy && (
        <Alert variant="destructive">
          <AlertTitle>Mailversand eingeschränkt</AlertTitle>
          <AlertDescription>
            {data.channelStatus === 'NotConfigured'
              ? 'Der technische Mailzugang ist noch nicht eingerichtet. Aufträge bleiben gespeichert.'
              : 'Microsoft 365 ist zurzeit nicht erreichbar. Es werden keine Eskalationsmails versucht.'}
          </AlertDescription>
        </Alert>
      )}
      <MetricCardGrid>
        <MetricCard
          label="Kanal"
          value={healthy ? 'Erreichbar' : 'Eingeschränkt'}
          tone={healthy ? 'success' : 'warning'}
        />
        <MetricCard label="Wartend" value={String(data.queuedCount)} />
        <MetricCard
          label="Endgültig fehlgeschlagen"
          value={String(data.failedCount)}
          tone={data.failedCount ? 'error' : 'neutral'}
        />
      </MetricCardGrid>
      <NotificationPolicyCard
        key={`${data.externalRecipientMode}:${data.allowedExternalDomains.join(',')}:${data.hasStoredExternalRecipientPolicy}`}
        status={data}
      />
      <Card>
        <CardHeader>
          <CardTitle>Konfiguration</CardTitle>
          <CardDescription>Geheimnisse werden hier nie angezeigt.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm">
            Absender: {data.senderDisplayName} &lt;
            {data.senderAddress || 'nicht eingerichtet'}&gt;
          </p>
          <p className="text-sm text-muted-foreground">
            Letzte Annahme:{' '}
            {data.lastAcceptedAt ? formatDateTime(data.lastAcceptedAt) : 'noch keine'}
          </p>
          <p className="text-sm text-muted-foreground">
            Offene Eskalationsfenster: {data.openEscalationCount}
            {data.affectedModules.length > 0
              ? ` (${data.affectedModules.join(', ')})`
              : ''}
          </p>
          <p className="text-sm text-muted-foreground">
            Dokumentanhänge:{' '}
            {data.attachmentStorageConfigured ? 'verfügbar' : 'nicht eingerichtet'}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <StatusBadge tone={data.isPaused ? 'warning' : 'success'}>
              {data.isPaused ? 'Global pausiert – kein Versand' : 'Global freigegeben'}
            </StatusBadge>
            <Button
              variant="outline"
              disabled={pause.isPending}
              onClick={() => pause.mutate(!data.isPaused)}
            >
              {data.isPaused ? 'Versand fortsetzen' : 'Versand pausieren'}
            </Button>
            <Button
              variant="outline"
              disabled={testMail.isPending || !data.isConfigured}
              onClick={() => testMail.mutate()}
            >
              Testmail an mich
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function FailedSection() {
  const api = useNotificationsApi()
  const queryClient = useQueryClient()
  const failed = useQuery({
    queryKey: ['notifications', 'failed'],
    queryFn: ({ signal }) =>
      api.get<FailedNotification[]>('/jobs/failed?skip=0&take=100', { signal }),
  })
  const retry = useMutation({
    mutationFn: (id: string) => api.post(`/jobs/${id}/retry`, {}),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['notifications'] })
      toast.success('Auftrag erneut eingereiht')
    },
  })
  const columns: DataTableColumn<FailedNotification>[] = [
    {
      key: 'origin',
      header: 'Urheber',
      cell: (row) => (row.originKind === 'Platform' ? 'Plattform' : row.originId),
    },
    { key: 'type', header: 'Art', cell: (row) => row.typeKey },
    { key: 'time', header: 'Zeitpunkt', cell: (row) => formatDateTime(row.updatedAt) },
    {
      key: 'attempts',
      header: 'Versuche',
      cell: (row) => row.attemptCount,
      align: 'right',
    },
    {
      key: 'error',
      header: 'Fehler',
      cell: (row) => row.lastError ?? 'Unbekannter Fehler',
    },
    {
      key: 'action',
      header: '',
      cell: (row) => (
        <Button
          size="sm"
          variant="outline"
          disabled={retry.isPending}
          onClick={() => retry.mutate(row.id)}
        >
          Wiederholen
        </Button>
      ),
    },
  ]
  return (
    <DataTable
      columns={columns}
      rows={failed.data ?? []}
      rowKey={(row) => row.id}
      caption="Endgültig fehlgeschlagene Benachrichtigungen"
      isLoading={failed.isPending}
      error={failed.isError ? { onRetry: () => failed.refetch() } : null}
    />
  )
}

function TypesSection() {
  const api = useNotificationsApi()
  const queryClient = useQueryClient()
  const status = useQuery({
    queryKey: ['notifications', 'status'],
    queryFn: ({ signal }) => api.get<NotificationStatus>('/status', { signal }),
  })
  const types = useQuery({
    queryKey: ['notifications', 'types'],
    queryFn: ({ signal }) => api.get<RegisteredNotificationType[]>('/types', { signal }),
  })
  const pause = useMutation({
    mutationFn: ({ key, paused }: { key: string; paused: boolean }) =>
      api.put(`/types/${encodeURIComponent(key)}/pause`, { paused }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['notifications', 'types'] })
      toast.success('Benachrichtigungsart gespeichert')
    },
  })
  const columns: DataTableColumn<RegisteredNotificationType>[] = [
    {
      key: 'origin',
      header: 'Urheber',
      cell: (row) => (row.originKind === 'Platform' ? 'Plattform' : row.originId),
    },
    {
      key: 'name',
      header: 'Art',
      cell: (row) => (
        <div>
          <p className="font-medium">{row.name}</p>
          <p className="text-xs text-muted-foreground">{row.key}</p>
        </div>
      ),
    },
    {
      key: 'mode',
      header: 'Modus',
      cell: (row) => (
        <StatusBadge tone={row.isMandatory ? 'info' : 'neutral'}>
          {row.isMandatory
            ? 'verpflichtend'
            : row.defaultEnabled
              ? 'optional · an'
              : 'optional · aus'}
        </StatusBadge>
      ),
    },
    {
      key: 'paused',
      header: 'Versandstatus',
      cell: (row) => (
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge
            tone={row.isPaused || status.data?.isPaused ? 'warning' : 'neutral'}
          >
            {row.isPaused
              ? 'Pausiert – kein Versand'
              : !status.data || status.isError
                ? 'Globaler Versandstatus unbekannt'
                : status.data.isPaused
                  ? 'Global pausiert – kein Versand'
                  : status.data.channelStatus !== 'Available'
                    ? 'Mailzugang eingeschränkt'
                    : 'Freigegeben'}
          </StatusBadge>
          <Button
            variant="outline"
            size="sm"
            aria-label={`${row.name}: ${row.isPaused ? 'Fortsetzen' : 'Pausieren'}`}
            disabled={pause.isPending}
            onClick={() => pause.mutate({ key: row.key, paused: !row.isPaused })}
          >
            {row.isPaused ? 'Fortsetzen' : 'Pausieren'}
          </Button>
        </div>
      ),
    },
  ]
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Verpflichtende Nachrichten können von Nutzern nicht abbestellt werden.
        Administrative Pausen stoppen auch diese Nachrichten. „Freigegeben“ bedeutet keine
        Zustellbestätigung; Empfängereinstellungen und Versandrichtlinien gelten
        weiterhin. Die Aktionen ändern nur die Pause der jeweiligen Art.
      </p>
      {status.isError ? (
        <Alert variant="destructive">
          <AlertTitle>Globaler Versandstatus nicht verfügbar</AlertTitle>
          <AlertDescription>
            Ob der gesamte Versand pausiert ist, konnte nicht geprüft werden.
            <Button variant="outline" onClick={() => void status.refetch()}>
              Erneut prüfen
            </Button>
          </AlertDescription>
        </Alert>
      ) : status.data?.isPaused ? (
        <Alert>
          <AlertTitle>Global pausiert – kein Versand</AlertTitle>
          <AlertDescription>
            Alle Benachrichtigungsarten sind vom Versand ausgeschlossen, auch
            verpflichtende. Das Fortsetzen einer einzelnen Art hebt die globale Pause
            nicht auf. Sie können diese unter „Betriebszustand“ aufheben.
          </AlertDescription>
        </Alert>
      ) : null}
      <DataTable
        columns={columns}
        rows={types.data ?? []}
        rowKey={(row) => row.key}
        caption="Registrierte Benachrichtigungsarten"
        isLoading={types.isPending}
        error={types.isError ? { onRetry: () => types.refetch() } : null}
      />
    </div>
  )
}

function ContactsSection() {
  const api = useNotificationsApi()
  const identityApi = usePlatformApi()
  const queryClient = useQueryClient()
  const [moduleId, setModuleId] = useState('')
  const [personSearchInput, setPersonSearchInput] = useState('')
  const [personSearch, setPersonSearch] = useState('')
  const [selectedPerson, setSelectedPerson] = useState<PlattformNutzer | null>(null)
  useEffect(() => {
    const timer = setTimeout(
      () => setPersonSearch(personSearchInput),
      PERSON_SEARCH_DEBOUNCE_MS,
    )
    return () => clearTimeout(timer)
  }, [personSearchInput])

  const types = useQuery({
    queryKey: ['notifications', 'types'],
    queryFn: ({ signal }) => api.get<RegisteredNotificationType[]>('/types', { signal }),
  })
  const modules = useMemo(
    () =>
      [
        ...new Set(
          (types.data ?? [])
            .filter((type) => type.originKind === 'Module')
            .map((type) => type.originId),
        ),
      ].sort(),
    [types.data],
  )
  const contacts = useQuery({
    queryKey: ['notifications', 'contacts', moduleId],
    queryFn: ({ signal }) =>
      api.get<string[]>(`/modules/${encodeURIComponent(moduleId)}/contacts`, { signal }),
    enabled: moduleId.length > 0,
  })
  const users = useQuery({
    queryKey: ['notifications', 'platform-users', personSearch],
    queryFn: ({ signal }) =>
      identityApi.get<PlattformNutzer[]>('/users', {
        query: personSearch ? { suche: personSearch } : undefined,
        signal,
      }),
    enabled: moduleId.length > 0,
  })
  const add = useMutation({
    mutationFn: () =>
      api.put(
        `/modules/${encodeURIComponent(moduleId)}/contacts/${encodeURIComponent(selectedPerson?.id ?? '')}`,
        {},
      ),
    onSuccess: async () => {
      setSelectedPerson(null)
      setPersonSearchInput('')
      setPersonSearch('')
      await queryClient.invalidateQueries({
        queryKey: ['notifications', 'contacts', moduleId],
      })
      toast.success('Verantwortlicher hinzugefügt')
    },
  })
  const remove = useMutation({
    mutationFn: (id: string) =>
      api.delete(
        `/modules/${encodeURIComponent(moduleId)}/contacts/${encodeURIComponent(id)}`,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['notifications', 'contacts', moduleId],
      })
      toast.success('Verantwortlicher entfernt')
    },
  })
  const knownUsers = users.data ?? []
  const selectableUsers = knownUsers.filter(
    (user) => !user.isBlocked && !(contacts.data ?? []).includes(user.id),
  )
  const contactName = new Map(knownUsers.map((user) => [user.id, user]))
  return (
    <Card>
      <CardHeader>
        <CardTitle>Modulverantwortliche</CardTitle>
        <CardDescription>
          Ohne Zuordnung werden Plattformadministratoren informiert.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Select value={moduleId} onValueChange={setModuleId}>
          <SelectTrigger aria-label="Modul auswählen">
            <SelectValue placeholder="Modul auswählen" />
          </SelectTrigger>
          <SelectContent>
            {modules.map((module) => (
              <SelectItem key={module} value={module}>
                {module}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {moduleId && (
          <>
            <div className="flex gap-2">
              <div className="min-w-0 flex-1">
                <Combobox<PlattformNutzer>
                  items={selectableUsers}
                  value={selectedPerson}
                  onChange={setSelectedPerson}
                  onSearch={setPersonSearchInput}
                  getKey={(user) => user.id}
                  getLabel={(user) => user.name}
                  getDescription={(user) => user.email}
                  placeholder="Verantwortliche Person suchen"
                  emptyText="Keine verfügbare Person gefunden"
                  loading={users.isFetching}
                />
              </div>
              <Button
                disabled={selectedPerson === null || add.isPending}
                onClick={() => add.mutate()}
              >
                Hinzufügen
              </Button>
            </div>
            <ul className="space-y-2">
              {(contacts.data ?? []).map((id) => {
                const person = contactName.get(id)
                return (
                  <li
                    key={id}
                    className="flex items-center justify-between rounded-md border p-3 text-sm"
                  >
                    <span>
                      {person ? (
                        <>
                          <span className="block font-medium">{person.name}</span>
                          <span className="block text-xs text-muted-foreground">
                            {person.email}
                          </span>
                        </>
                      ) : (
                        id
                      )}
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={remove.isPending}
                      onClick={() => remove.mutate(id)}
                    >
                      Entfernen
                    </Button>
                  </li>
                )
              })}
            </ul>
            {contacts.data?.length === 0 && (
              <p className="text-sm text-muted-foreground">
                Keine Zuordnung — Fallback an platform.admin.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
